import { getFileState, useFileStore } from "../stores/fileStore";
import type { FileStoreState } from "../stores/fileStore/types";
import {
  getHistoryActions,
  makeHistorySnapshot,
  type HistorySnapshot,
} from "../stores/historyStore";
import { getProjectSaveActions, getProjectSaveState } from "../stores/projectSaveStore";
import { getSceneActions, getSceneState } from "../stores/sceneStore";
import { buildProjectSaveSnapshotHash } from "./projectSaveSnapshot";
import { isReadOnlyProjectMode, setReadOnlyProjectMode } from "./physicsGuards";

type DiscardedSceneSnapshot = {
  history: HistorySnapshot;
  fileState: Pick<
    FileStoreState,
    | "xmlFile"
    | "fileMap"
    | "referencedFiles"
    | "missingFiles"
    | "keyframes"
    | "keyframeDetails"
    | "preservedMjcfSections"
    | "pendingMjcfImportWarnings"
    | "projectName"
    | "isNewProject"
    | "currentLoadingFolder"
  >;
  projectSaveState: ReturnType<typeof getProjectSaveState>;
  readOnlyProjectMode: boolean;
};

export const RESTORE_DISCARDED_SCENE_EVENT = "critter-restore-discarded-scene";

let discardedSceneSnapshot: DiscardedSceneSnapshot | null = null;
let listenerInstalled = false;

const hasRecordEntries = (value: unknown): boolean =>
  Boolean(
    value && typeof value === "object" && Object.keys(value as Record<string, unknown>).length > 0
  );

const hasArrayEntries = (value: unknown): boolean => Array.isArray(value) && value.length > 0;

const hasMeaningfulSceneContent = (snapshot: HistorySnapshot): boolean => {
  const scene = snapshot.scene;
  return Boolean(
    hasRecordEntries(scene.bodies) ||
    hasArrayEntries(scene.worldBodies) ||
    hasRecordEntries(scene.joints) ||
    hasRecordEntries(scene.sites) ||
    hasRecordEntries(scene.cameras) ||
    hasRecordEntries(scene.meshes) ||
    hasRecordEntries(scene.hfields) ||
    hasRecordEntries(scene.tendons) ||
    hasRecordEntries(scene.actuators) ||
    hasRecordEntries(scene.sensors) ||
    hasRecordEntries(scene.equality) ||
    hasArrayEntries(scene.contactPairs) ||
    hasArrayEntries(scene.contactExcludes)
  );
};

const dispatchRecoveryToast = () => {
  window.dispatchEvent(
    new CustomEvent("critter-toast", {
      detail: {
        message: "Previous scene replaced in the editor.",
        action: {
          label: "Restore previous",
          event: RESTORE_DISCARDED_SCENE_EVENT,
        },
        durationMs: 12000,
      },
    })
  );
};

const restoreDiscardedScene = () => {
  if (!discardedSceneSnapshot) return;
  const snapshot = discardedSceneSnapshot;
  discardedSceneSnapshot = null;
  const beforeRestore = makeHistorySnapshot(getSceneState());

  getSceneActions().restoreState(snapshot.history.scene);
  useFileStore.setState({
    ...snapshot.fileState,
    keyframes: snapshot.history.keyframes || snapshot.fileState.keyframes || {},
  });
  setReadOnlyProjectMode(snapshot.readOnlyProjectMode);

  getHistoryActions().clearHistory();
  getHistoryActions().save(beforeRestore, "Restored previous scene");

  const saveActions = getProjectSaveActions();
  const { lastSavedSnapshotHash, lastSaveTarget, saveStatus } = snapshot.projectSaveState;
  saveActions.resetProjectSaveState();
  if (lastSavedSnapshotHash) {
    saveActions.markProjectSaved(lastSavedSnapshotHash, lastSaveTarget || "import");
  }
  const restoredSnapshotHash = buildProjectSaveSnapshotHash({
    scene: getSceneState(),
    fileState: useFileStore.getState(),
  });
  if (!lastSavedSnapshotHash || restoredSnapshotHash !== lastSavedSnapshotHash) {
    saveActions.markProjectDirty();
  } else if (saveStatus === "error") {
    saveActions.markProjectSaveError("Previous save error");
  }

  try {
    window.dispatchEvent(new Event("structure-changed"));
  } catch {
    // Best-effort UI notification; the restored state is already applied.
  }
};

const ensureRecoveryListener = () => {
  if (listenerInstalled || typeof window === "undefined") return;
  listenerInstalled = true;
  window.addEventListener(RESTORE_DISCARDED_SCENE_EVENT, restoreDiscardedScene);
};

export const rememberDiscardedSceneForRestore = (): void => {
  if (typeof window === "undefined") return;
  const history = makeHistorySnapshot(getSceneState());
  if (!hasMeaningfulSceneContent(history)) return;
  const readOnlyProjectMode = isReadOnlyProjectMode();
  if (discardedSceneSnapshot?.readOnlyProjectMode && !readOnlyProjectMode) return;
  const fileState = getFileState();
  discardedSceneSnapshot = {
    history,
    projectSaveState: getProjectSaveState(),
    readOnlyProjectMode,
    fileState: {
      xmlFile: fileState.xmlFile,
      fileMap: fileState.fileMap || {},
      referencedFiles: fileState.referencedFiles || [],
      missingFiles: fileState.missingFiles || [],
      keyframes: fileState.keyframes || {},
      keyframeDetails: fileState.keyframeDetails || {},
      preservedMjcfSections: fileState.preservedMjcfSections || {},
      pendingMjcfImportWarnings: fileState.pendingMjcfImportWarnings,
      projectName: fileState.projectName,
      isNewProject: fileState.isNewProject,
      currentLoadingFolder: fileState.currentLoadingFolder,
    },
  };
  ensureRecoveryListener();
  dispatchRecoveryToast();
};
